import {
  ELegalDocumentKind,
  type ILegalDocumentVersion,
} from '@mahawi/eshop-common/dist/src/types';

export const LEGAL_VERSIONS: ILegalDocumentVersion[] = [
  {
    kind: ELegalDocumentKind.TERMS,
    versionCode: '2026-10-20',
    namespace: 'legal-terms-2026-10-20',
  },
  {
    kind: ELegalDocumentKind.PRIVACY_POLICY,
    versionCode: '2026-10-20',
    namespace: 'legal-privacy-2026-10-20',
  },
];

export const getVersion = (
  kind: ELegalDocumentKind,
  versionCode: string,
): ILegalDocumentVersion | null =>
  LEGAL_VERSIONS.find(
    (version: ILegalDocumentVersion): boolean =>
      version.kind === kind && version.versionCode === versionCode,
  ) ?? null;

export const getCurrentVersion = (
  kind: ELegalDocumentKind,
): ILegalDocumentVersion => {
  const current: ILegalDocumentVersion | undefined = LEGAL_VERSIONS.filter(
    (version: ILegalDocumentVersion): boolean => version.kind === kind,
  ).at(-1);

  if (!current) {
    throw new Error(`No legal document version for kind ${kind}.`);
  }

  return current;
};

export const isAcceptableTermsVersion = (versionCode: string): boolean =>
  getCurrentVersion(ELegalDocumentKind.TERMS).versionCode === versionCode;

export const isPublishedVersion = (
  kind: ELegalDocumentKind,
  versionCode: string,
): boolean => getVersion(kind, versionCode) !== null;

export const getAllLegalNamespaces = (): string[] =>
  LEGAL_VERSIONS.map(
    (version: ILegalDocumentVersion): string => version.namespace,
  );

const LEGAL_PATH_PATTERN =
  /^\/[a-z]{2}\/(terms-and-conditions|privacy-policy)(?:\/([^/?#]+))?\/?$/;

export const getLegalNamespacesForPath = (pathname: string): string[] => {
  const match: RegExpExecArray | null = LEGAL_PATH_PATTERN.exec(pathname);

  if (!match) {
    return [];
  }

  const kind: ELegalDocumentKind =
    match[1] === 'privacy-policy'
      ? ELegalDocumentKind.PRIVACY_POLICY
      : ELegalDocumentKind.TERMS;
  const versionCode: string | undefined = match[2];
  const version: ILegalDocumentVersion | null = versionCode
    ? getVersion(kind, versionCode)
    : getCurrentVersion(kind);
  const privacyNamespace: string = getCurrentVersion(
    ELegalDocumentKind.PRIVACY_POLICY,
  ).namespace;

  return [...new Set([version?.namespace, privacyNamespace])].filter(
    (namespace: string | undefined): namespace is string => Boolean(namespace),
  );
};
